import { Component, computed, effect, ElementRef, inject, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmitterService, EmittingDataEnum } from '@client-monorepo/common/utilities';
import { NgxBottomNavigationService } from '@digipay/ngx-bottom-navigation';
import { Subscription } from 'rxjs';
import { BackHandlerService } from '@client-monorepo/back-handler';
import { NgxAppBarButtonType, NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { TimerCountDownModel } from '@digipay/ngx-count-down';

export type TitleBarActionImage =
  | 'qr-scan'
  | 'play'
  | 'info-circle'
  | 'headphone'
  | 'checklist'
  | 'edit-square'
  | 'download'
  | 'bag'
  | 'search'
  | '';

@Component({
  selector: 'common-ui-components-page-layout',
  standalone: true,
  imports: [CommonModule, NgxAppBarComponent],
  templateUrl: './page-layout.component.html',
  styleUrl: './page-layout.component.scss',
})
export class PageLayoutComponent implements OnInit, OnDestroy {
  private emitterService = inject(EmitterService);
  private elementRef = inject(ElementRef);
  private bottomNavigationService = inject(NgxBottomNavigationService);
  private backHandler = inject(BackHandlerService);

  // Inputs
  title = input<string>();
  stickyHeader = input<boolean>(true);
  showBorder = input<boolean>(true);
  showBackIcon = input<boolean>(true);
  hasBackAction = input<boolean>(true);
  headerClasses = input<string>('surface-elevated');
  bodyClasses = input<string>('surface-elevated');
  infoButton = input<boolean>();
  actionImage = input<TitleBarActionImage>();
  actionText = input<string | null | boolean>();
  actionTextMode = input<'info' | 'error'>('info');
  leftButtonDisabled = input<boolean>(false);
  leftIcon = input<string>('');
  leftTimer = input<TimerCountDownModel | undefined>();
  closeButton = input<boolean>();

  // Outputs
  onBackAction = output<void>();
  onCustomAction = output<void>();
  infoClick = output<void>();
  onTextClick = output<void>();
  onLeftIconClick = output<void>();
  onFinishRightTimer = output<void>();
  onFinishLeftTimer = output<void>();

  // View Child
  pageBody = viewChild<ElementRef>('pageBody');

  // Component state
  hasScrolled = signal<boolean>(false);
  initialHeight = 0;
  emitted = false;
  private routerSubscription?: Subscription;
  private scrollCheckTimeout?: any;

  // Computed properties
  bodyStyle = computed(() => {
    const padding = this.hasScrolled() ? this.bottomNavigationService.reservedHeight() : 0;
    return { paddingBottom: `${padding}px` };
  });

  rightButton = computed(() => {
    const button: NgxAppBarButtonType = {};

    if (this.showBackIcon()) {
      button.icon = 'arrow-right';
    }

    if (this.closeButton()) {
      button.icon = 'close';
    }

    button.size = 'medium';
    button.mode = 'icon-only';
    button.style = 'neutral-link';

    return button.icon || button.label ? button : undefined;
  });

  leftButton = computed(() => {
    const button: NgxAppBarButtonType = {};

    if (this.infoButton()) {
      button.icon = 'info-circle';
    } else if (this.actionImage() && this.actionImage() !== '') {
      button.icon = this.actionImage();
    }

    if (this.actionText()) {
      button.label = this.actionText()?.toString();
    } else {
      button.mode = 'icon-only';
    }

    button.size = 'medium';

    if (this.actionTextMode() === 'error') {
      button.destructive = true;
      button.style = 'link';
    } else if (this.actionTextMode() === 'info') {
      button.style = 'link';
    }

    if (this.leftButtonDisabled()) {
      button.disabled = true;
    }

    if (this.leftTimer()) {
      button.timer = this.leftTimer();
    }

    return button.icon || button.label ? button : null;
  });

  constructor() {
    effect(
      () => {
        if (this.pageBody()) {
          if (this.scrollCheckTimeout) {
            clearTimeout(this.scrollCheckTimeout);
          }
          this.scrollCheckTimeout = setTimeout(() => this.checkScroll());
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    const element = this.elementRef?.nativeElement?.ownerDocument.body;
    this.initialHeight = element.clientHeight;
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    if (this.scrollCheckTimeout) {
      clearTimeout(this.scrollCheckTimeout);
    }
  }

  onLeftButtonClick(event: any): void {
    if (this.infoButton()) {
      this.infoClick.emit(event);
    }

    if (this.actionText()) {
      this.onTextClick.emit(event);
    }
  }

  onLeftIcon(event: any): void {
    this.onLeftIconClick.emit(event);
  }

  handleActionClick(): void {
    if (this.hasBackAction()) {
      this.backHandler.goBack();
    }
    this.onBackAction.emit();
  }

  handleScroll(event: any): void {
    if (this.pageBody()?.nativeElement === event.currentTarget) {
      const element = this.pageBody()?.nativeElement;
      const scrollPosition = element.scrollTop + element.offsetHeight;
      const maxScroll = element.scrollHeight;

      if (scrollPosition >= maxScroll - 50) {
        if (!this.emitted) {
          this.emitterService.emitEvent(EmittingDataEnum.PAGE_LAYOUT_SCROLLED_TO_END);
          this.emitted = true;
          this.checkScroll();
        }
      } else {
        this.emitted = false;
      }
    }
  }

  private checkScroll(): void {
    const element = this.pageBody()?.nativeElement;
    const availableHeight = this.initialHeight - this.bottomNavigationService.reservedHeight();
    this.hasScrolled.set(element.clientHeight > availableHeight);
  }
}
